import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { QK } from '../shared/model/queryKeys';
import {
  tradingSpecsApi,
  type DraftRequestV1,
  type IndicatorSpecV1,
  type QuickSaveRequest,
  type SpecKind,
  type StrategySpecV1,
} from '../shared/api/tradingSpecs';

export const useStrategySpecsCatalog = (status = 'READY', enabled = true) => {
  return useQuery({
    queryKey: QK.tradingSpecsCatalog('strategy', status),
    queryFn: () => tradingSpecsApi.listStrategyCatalog(status),
    enabled,
    staleTime: 1000 * 60 * 2,
  });
};

export const useIndicatorSpecsCatalog = (status = 'READY', enabled = true) => {
  return useQuery({
    queryKey: QK.tradingSpecsCatalog('indicator', status),
    queryFn: () => tradingSpecsApi.listIndicatorCatalog(status),
    enabled,
    staleTime: 1000 * 60 * 2,
  });
};

export const useStrategySpecsFailedCatalog = () => useStrategySpecsCatalog('FAILED');
export const useIndicatorSpecsFailedCatalog = () => useIndicatorSpecsCatalog('FAILED');

export const useCreateStrategyDraft = () => {
  return useMutation({
    mutationFn: (body: DraftRequestV1) => tradingSpecsApi.createStrategyDraft(body),
  });
};

export const useCreateIndicatorDraft = () => {
  return useMutation({
    mutationFn: (body: DraftRequestV1) => tradingSpecsApi.createIndicatorDraft(body),
  });
};

export const useValidateStrategySpec = () => {
  return useMutation({
    mutationFn: (body: StrategySpecV1) => tradingSpecsApi.validateStrategy(body),
  });
};

export const useValidateIndicatorSpec = () => {
  return useMutation({
    mutationFn: (body: IndicatorSpecV1) => tradingSpecsApi.validateIndicator(body),
  });
};

export const useSaveStrategySpec = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: StrategySpecV1) => tradingSpecsApi.saveStrategy(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.tradingSpecsCatalog('strategy', 'READY') });
    },
  });
};

export const useSaveIndicatorSpec = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IndicatorSpecV1) => tradingSpecsApi.saveIndicator(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.tradingSpecsCatalog('indicator', 'READY') });
    },
  });
};

export const useQuickSaveIndicator = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: QuickSaveRequest) => tradingSpecsApi.quickSaveIndicator(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.tradingSpecsCatalog('indicator', 'READY') });
    },
  });
};

export const useQuickSaveStrategy = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: QuickSaveRequest) => tradingSpecsApi.quickSaveStrategy(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.tradingSpecsCatalog('strategy', 'READY') });
    },
  });
};

export const useDeprecateTradingSpecVersion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      kind: SpecKind;
      specId: string;
      version: string;
      reason?: string;
      force?: boolean;
    }) =>
      tradingSpecsApi.deprecateSpecVersion(args.kind, args.specId, args.version, {
        ...(args.reason !== undefined ? { reason: args.reason } : {}),
        ...(args.force !== undefined ? { force: args.force } : {}),
      }),
    onSuccess: (_data, args) => {
      qc.invalidateQueries({ queryKey: QK.tradingSpecsCatalog(args.kind, 'READY') });
      qc.invalidateQueries({ queryKey: QK.tradingSpecsCatalog(args.kind, 'FAILED') });
      qc.invalidateQueries({ queryKey: QK.tradingSpecVersions(args.kind, args.specId) });
    },
  });
};

export const useSpecVersions = (kind: SpecKind, specId: string | null) => {
  const validKind = kind === 'indicator' || kind === 'strategy';
  const sid = String(specId ?? '').trim();
  return useQuery({
    queryKey: QK.tradingSpecVersions(kind, sid),
    queryFn: () => tradingSpecsApi.listVersions(kind, sid),
    enabled: validKind && sid.length > 0,
    staleTime: 1000 * 60,
  });
};

export const useTradingSpecVersion = (
  kind: SpecKind,
  specId: string | null,
  version: string | null,
) => {
  const validKind = kind === 'indicator' || kind === 'strategy';
  const sid = String(specId ?? '').trim();
  const ver = String(version ?? '').trim();
  return useQuery({
    queryKey: ['trading-spec', kind, sid, ver],
    queryFn: () => tradingSpecsApi.getSpecVersion(kind, sid, ver),
    enabled: validKind && sid.length > 0 && ver.length > 0,
    staleTime: 1000 * 30,
  });
};

export const useStrategyResolvedSpec = (
  specId: string | null,
  version: string | null,
  mode: 'agent' | 'backtest' | 'opt' = 'agent',
) => {
  return useQuery({
    queryKey: QK.tradingSpecResolved(specId ?? '', version ?? '', mode),
    queryFn: () => tradingSpecsApi.resolveStrategy(specId!, version!, mode),
    enabled: !!specId && !!version,
    staleTime: 1000 * 30,
  });
};

